/**
 * ConnectionSetupWizard - Generic wizard component for configuring integration connections
 * Uses JSON Forms with ActionSelectControl for dynamic form rendering
 */

import {
  getConnectionSettings,
  useUpdateConnectionConfig,
} from "@/api/integrations";
import { useUserInfo } from "@/api/user";
import { useOrgUsers } from "@/api/users";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import Sentry from "@/integrations/Sentry";
import { cells, renderers } from "@/jsonforms";
import {
  ActionSelectDirect,
  ActionSelectOptions,
} from "@/jsonforms/controls/ActionSelectControl";
import {
  IntegrationConnection,
  ToolSetConfigurationSchema,
  UserSettingsContainer,
} from "@cohesive/api-client";
import { JsonForms } from "@jsonforms/react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

interface ConnectionSetupWizardProps {
  connection: IntegrationConnection;
  schema: ToolSetConfigurationSchema;
  authLevel: "organization" | "user";
  mode?: "setup" | "manage";
  onComplete?: () => void;
}

export function ConnectionSetupWizard({
  connection,
  schema,
  authLevel,
  mode = "setup",
  onComplete,
}: ConnectionSetupWizardProps) {
  const isSetupMode = mode === "setup";
  const { data: userInfo } = useUserInfo();
  const isAdmin = useIsOrgAdmin();
  const queryClient = useQueryClient();
  const { data: orgUsers } = useOrgUsers();

  const updateConnectionConfig = useUpdateConnectionConfig();

  // For user-level auth, store everything in settings (no user_settings nesting)
  const isUserLevelAuth = authLevel === "user";

  // Form state for settings
  const [settings, setSettings] = useState<Record<string, any>>(
    connection.config?.settings || {},
  );

  // Form state for user_settings (with user_mapping wrapper) - only for org-level auth
  const [allUserSettings, setAllUserSettings] = useState<UserSettingsContainer>(
    connection.config?.user_settings || {},
  );

  const [isSaving, setIsSaving] = useState(false);

  // Track form validation errors from JsonForms
  // Using any[] since JsonForms returns AJV error objects
  const [settingsErrors, setSettingsErrors] = useState<any[]>([]);
  const [userSettingsErrors, setUserSettingsErrors] = useState<any[]>([]);

  // Dialog state for user mapping warning
  const [showUserMappingWarning, setShowUserMappingWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<"save" | "skip" | null>(
    null,
  );

  // Initialize form with existing config
  useEffect(() => {
    const existingSettings = getConnectionSettings(connection);
    const existingUserSettings = connection.config?.user_settings;

    // Always set state to ensure sync, even when switching to a connection with no config
    setSettings(existingSettings || {});
    if (!isUserLevelAuth) {
      setAllUserSettings(existingUserSettings || {});
    }
  }, [connection, isUserLevelAuth]);

  // Determine which sections to show
  // For user-level auth: only settings (owner configures everything)
  // For org-level auth: settings (admin only) + user_settings (table for admin, single for user)
  const showSettingsFields = isUserLevelAuth
    ? schema.settings?.ui_schema
    : isAdmin && schema.settings?.ui_schema;

  // user_settings only applies to org-level auth
  const showUserSettingsFields =
    !isUserLevelAuth && schema.user_settings?.ui_schema;

  // Inject integrationConnectionId into ui_schema options for ActionSelectControl
  const settingsUiSchema = useMemo(() => {
    if (!schema.settings?.ui_schema) return undefined;
    return injectConnectionId(schema.settings.ui_schema, connection.id);
  }, [schema.settings?.ui_schema, connection.id]);

  const userSettingsUiSchema = useMemo(() => {
    if (!schema.user_settings?.ui_schema) return undefined;
    return injectConnectionId(schema.user_settings.ui_schema, connection.id);
  }, [schema.user_settings?.ui_schema, connection.id]);

  // Helper to update a specific user's settings in the user_mapping
  const updateUserSetting = (userId: string, fieldKey: string, value: any) => {
    setAllUserSettings((prev) => ({
      ...prev,
      user_mapping: {
        ...prev.user_mapping,
        [userId]: {
          ...prev.user_mapping?.[userId],
          [fieldKey]: value,
        },
      },
    }));
  };

  // Helper to clear a specific user's settings from user_mapping
  const clearUserSettings = (userId: string) => {
    setAllUserSettings((prev) => {
      if (!prev.user_mapping) return prev;
      const updatedMapping = { ...prev.user_mapping };
      delete updatedMapping[userId];
      return { ...prev, user_mapping: updatedMapping };
    });
  };

  // Check if user_mapping has any configured users
  const hasUserMappingConfigured = useMemo(() => {
    const userMapping = allUserSettings?.user_mapping;
    if (!userMapping || typeof userMapping !== "object") return false;

    // Check if at least one user has any non-empty field configured
    return Object.values(userMapping).some((mapping: any) => {
      if (!mapping || typeof mapping !== "object") return false;
      return Object.values(mapping).some(
        (value) => value !== null && value !== undefined && value !== "",
      );
    });
  }, [allUserSettings]);

  // Check if form has validation errors
  const hasFormErrors = useMemo(() => {
    return settingsErrors.length > 0 || userSettingsErrors.length > 0;
  }, [settingsErrors, userSettingsErrors]);

  // Format error messages for display
  const formErrorMessages = useMemo(() => {
    const allErrors = [...settingsErrors, ...userSettingsErrors];
    // Deduplicate and format error messages
    const uniqueMessages = [
      ...new Set(allErrors.map((e) => e.message).filter(Boolean)),
    ];
    return uniqueMessages;
  }, [settingsErrors, userSettingsErrors]);

  // Execute the pending action after user confirms in warning dialog
  const executePendingAction = async () => {
    setShowUserMappingWarning(false);
    if (pendingAction === "save") {
      await performSave();
    } else if (pendingAction === "skip") {
      await performSkip();
    }
    setPendingAction(null);
  };

  // Close the warning dialog without executing action
  const cancelPendingAction = () => {
    setShowUserMappingWarning(false);
    setPendingAction(null);
  };

  // The actual save implementation
  const performSave = async () => {
    setIsSaving(true);
    try {
      // Build the complete config update
      const configUpdate: any = {};

      // Add settings
      if (showSettingsFields && Object.keys(settings).length > 0) {
        configUpdate.settings = settings;
      }

      // Add user_settings (org-level auth only)
      if (
        showUserSettingsFields &&
        allUserSettings.user_mapping &&
        Object.keys(allUserSettings.user_mapping).length > 0
      ) {
        configUpdate.user_settings = allUserSettings;
      }

      // Save everything in one call
      if (Object.keys(configUpdate).length > 0) {
        await updateConnectionConfig.mutateAsync({
          integrationConnectionId: connection.id,
          config: configUpdate,
          authLevel,
        });

        // Wait for queries to refetch so UI has fresh data
        await queryClient.refetchQueries({
          queryKey: ["integrationConnections"],
        });
      }

      toast.success(
        isSetupMode
          ? "Setup complete! You can now use this integration."
          : "Configuration saved successfully",
      );
      onComplete?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for save button - checks for user_mapping warning first
  const handleSave = () => {
    // Check if user_mapping is required but not configured
    if (showUserSettingsFields && isAdmin && !hasUserMappingConfigured) {
      setPendingAction("save");
      setShowUserMappingWarning(true);
      return;
    }
    performSave();
  };

  // The actual skip implementation
  const performSkip = async () => {
    if (!isSetupMode) return;
    setIsSaving(true);
    try {
      // Save a flag to indicate setup was dismissed
      const configUpdate: any = {};

      // For user-level auth or org-level auth as admin, save to settings
      if (isUserLevelAuth || isAdmin) {
        configUpdate.settings = {
          ...settings,
          _setup_dismissed: true,
        };
      } else if (userInfo?.user?.id) {
        // For org-level auth as regular user, save to their user_settings
        configUpdate.user_settings = {
          user_mapping: {
            [userInfo.user.id]: {
              ...allUserSettings.user_mapping?.[userInfo.user.id],
              _setup_dismissed: true,
            },
          },
        };
      }

      if (Object.keys(configUpdate).length > 0) {
        await updateConnectionConfig.mutateAsync({
          integrationConnectionId: connection.id,
          config: configUpdate,
          authLevel,
        });

        // Wait for queries to refetch so UI has fresh data
        await queryClient.refetchQueries({
          queryKey: ["integrationConnections"],
        });
      }

      onComplete?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to skip setup");
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for skip button - checks for user_mapping warning first
  const handleSkip = () => {
    if (!isSetupMode) return;
    // Check if user_mapping is required but not configured
    if (showUserSettingsFields && isAdmin && !hasUserMappingConfigured) {
      setPendingAction("skip");
      setShowUserMappingWarning(true);
      return;
    }
    performSkip();
  };

  // Don't render if there's nothing to configure
  const hasSettingsFields = Boolean(showSettingsFields);
  const hasUserSettingsFields = Boolean(showUserSettingsFields);

  if (!hasSettingsFields && !hasUserSettingsFields) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {isSetupMode ? "Connection Setup" : "Connection Settings"}
          </CardTitle>
          <CardDescription>
            {isSetupMode
              ? "Configure your connection settings to get started"
              : "Manage your connection configuration"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Settings fields - rendered with JSON Forms */}
          {showSettingsFields && settingsUiSchema && (
            <div className="space-y-2">
              <h3 className="font-semibold">
                {isUserLevelAuth
                  ? "Connection Settings"
                  : "Organization Settings"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isUserLevelAuth
                  ? "Configure your connection preferences"
                  : "These settings apply to all users in your organization"}
              </p>
              <JsonForms
                schema={schema.settings!.schema}
                uischema={settingsUiSchema}
                data={settings}
                renderers={renderers}
                cells={cells}
                onChange={({ data, errors }) => {
                  setSettings(data || {});
                  setSettingsErrors(errors ?? []);
                }}
              />
            </div>
          )}

          {/* User settings fields (org-level auth only) - show table for admins */}
          {showUserSettingsFields &&
            isAdmin &&
            orgUsers &&
            schema.user_settings?.ui_schema && (
              <div className="space-y-2">
                <h3 className="font-semibold">User Mapping</h3>
                <p className="text-sm text-muted-foreground">
                  Configure settings for each team member
                </p>
                <UserMappingTable
                  users={orgUsers}
                  uiSchema={schema.user_settings.ui_schema}
                  userSettings={allUserSettings}
                  onUpdateUserSetting={updateUserSetting}
                  onClearUserSettings={clearUserSettings}
                  connectionId={connection.id}
                />
              </div>
            )}

          {/* User settings fields (org-level auth only) - non-admins see their own settings */}
          {showUserSettingsFields &&
            !isAdmin &&
            userInfo?.user?.id &&
            userSettingsUiSchema && (
              <div className="space-y-2">
                <h3 className="font-semibold">Personal Settings</h3>
                <p className="text-sm text-muted-foreground">
                  These settings are specific to you
                </p>
                <JsonForms
                  schema={schema.user_settings!.schema}
                  uischema={userSettingsUiSchema}
                  data={allUserSettings.user_mapping?.[userInfo.user!.id] || {}}
                  renderers={renderers}
                  cells={cells}
                  onChange={({ data, errors }) => {
                    if (userInfo?.user?.id) {
                      setAllUserSettings((prev) => ({
                        ...prev,
                        user_mapping: {
                          ...prev.user_mapping,
                          [userInfo.user!.id]: data || {},
                        },
                      }));
                    }
                    setUserSettingsErrors(errors ?? []);
                  }}
                />
              </div>
            )}

          {/* Form validation errors */}
          {hasFormErrors && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">
                    Please fix the following errors:
                  </p>
                  <ul className="text-sm text-destructive/90 list-disc list-inside">
                    {formErrorMessages.map((message, idx) => (
                      <li key={idx}>{message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            {isSetupMode && (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={isSaving || hasFormErrors}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Skip for now
              </Button>
            )}
            <Button onClick={handleSave} disabled={isSaving || hasFormErrors}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSetupMode ? "Save & Continue" : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Mapping Warning Dialog */}
      <AlertDialog
        open={showUserMappingWarning}
        onOpenChange={setShowUserMappingWarning}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-lc-warning" />
              No User Mappings Configured
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              This integration works better when you configure user mappings.
              Without mappings, activities may not be correctly attributed to
              team members.
              <br />
              <br />
              Are you sure you want to continue without configuring user
              mappings?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelPendingAction}>
              Go Back
            </AlertDialogCancel>
            <AlertDialogAction onClick={executePendingAction}>
              Continue Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Helper to inject integrationConnectionId into all Control elements in ui_schema
function injectConnectionId(uiSchema: any, connectionId: string): any {
  if (!uiSchema) return uiSchema;

  if (uiSchema.type === "Control") {
    return {
      ...uiSchema,
      options: {
        ...uiSchema.options,
        integrationConnectionId: connectionId,
      },
    };
  }

  if (uiSchema.elements) {
    return {
      ...uiSchema,
      elements: uiSchema.elements.map((el: any) =>
        injectConnectionId(el, connectionId),
      ),
    };
  }

  return uiSchema;
}

// Extract field definitions from ui_schema elements
interface FieldDef {
  key: string;
  label: string;
  options: any;
}

function extractFieldsFromUiSchema(uiSchema: any): FieldDef[] {
  const fields: FieldDef[] = [];

  function traverse(element: any) {
    if (element.type === "Control" && element.scope) {
      // Extract field key from scope like "#/properties/owner_id"
      const match = element.scope.match(/#\/properties\/(.+)/);
      if (match) {
        fields.push({
          key: match[1],
          label: element.label || match[1],
          options: element.options || {},
        });
      }
    }
    if (element.elements) {
      element.elements.forEach(traverse);
    }
  }

  traverse(uiSchema);
  return fields;
}

// User mapping table component for admin configuration
// ActionSelectDirect uses useToolboxQuery internally, which handles caching via React Query
// All instances with the same connectionId + actionName + params share the same cached data
//
// NOTE: Right now, the user mapping object must comform to the schema:
//
// {
//   "user_mapping": {
//     "b8e9280d-0d7d-49a3-b278-e329997ccca2": {
//       "owner_id": "79231858"
//     },
//     "db7af6e4-43f3-4710-8fbc-d764d9d28a08": {
//       "owner_id": "1574486917"
//     }
//   }
// }
//
interface UserMappingTableProps {
  users: Array<{ id: string; name: string; email: string }>;
  uiSchema: any;
  userSettings: UserSettingsContainer;
  onUpdateUserSetting: (userId: string, fieldKey: string, value: any) => void;
  onClearUserSettings: (userId: string) => void;
  connectionId: string;
}

function UserMappingTable({
  users,
  uiSchema,
  userSettings,
  onUpdateUserSetting,
  onClearUserSettings,
  connectionId,
}: UserMappingTableProps) {
  // Extract columns from ui_schema options.columns
  const fields = useMemo(() => {
    const uiFields = extractFieldsFromUiSchema(uiSchema);
    const userMappingControl = uiFields.find(
      (f) => f.options?.control === "user_mapping_table",
    );

    // If columns are explicitly defined in the ui_schema, use them
    if (userMappingControl?.options?.columns) {
      return userMappingControl.options.columns.map((col: any) => ({
        key: col.key,
        label: col.label || col.key,
        options: col,
      }));
    }

    // Fallback to extracting from ui_schema elements
    return uiFields;
  }, [uiSchema]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[200px]">User</TableHead>
          {fields.map((field: FieldDef) => (
            <TableHead key={field.key}>{field.label}</TableHead>
          ))}
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id}>
            <TableCell>
              <div className="flex flex-col">
                <span className="font-medium">{user.name}</span>
                <span className="text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
            </TableCell>
            {fields.map((field: FieldDef) => {
              const opts = field.options as ActionSelectOptions | undefined;

              // Use ActionSelectDirect for action_select controls
              // React Query handles caching - all instances share the same cached data
              if (opts?.control === "action_select") {
                return (
                  <TableCell key={field.key}>
                    <ActionSelectDirect
                      value={userSettings.user_mapping?.[user.id]?.[field.key]}
                      onChange={(value) =>
                        onUpdateUserSetting(
                          user.id,
                          field.key,
                          value as string | undefined,
                        )
                      }
                      options={opts}
                      integrationConnectionId={connectionId}
                      compact
                    />
                  </TableCell>
                );
              }

              // Default to text input for other types
              return (
                <TableCell key={field.key}>
                  <input
                    type="text"
                    value={
                      userSettings.user_mapping?.[user.id]?.[field.key] || ""
                    }
                    onChange={(e) =>
                      onUpdateUserSetting(user.id, field.key, e.target.value)
                    }
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    placeholder="Enter value..."
                  />
                </TableCell>
              );
            })}
            <TableCell>
              {userSettings.user_mapping?.[user.id] &&
                Object.keys(userSettings.user_mapping[user.id]).length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onClearUserSettings(user.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
